/**
 * Selector de cantidad: menos, el numero, mas.
 *
 * El numero es un `<input>` de verdad y no un texto, porque pedir doce
 * unidades a golpe de "+" no es razonable para quien equipa una operacion de rafting. Se escribe libre y
 * se acota al salir del campo: acotar en cada tecla impide borrar el 1 para
 * escribir 12.
 *
 * El grupo `.contador` es el del prototipo: los dos botones y el campo dentro
 * de un mismo borde. Ya venia portado en `ficha.css` y sin usar, esperando
 * justamente esta rebanada.
 */
import { useEffect, useState } from 'react';

import { MAXIMO_POR_LINEA } from '../../utilidades/carro';
import { Icono } from '../comunes/Icono';


export interface SelectorCantidadProps {
  cantidad: number;
  /** Nombre del producto: es lo que distingue este control de los demas de la lista. */
  nombre: string;
  /**
   * Desde donde "menos" deja de restar. En el carro no hay (bajar de 1 quita
   * la linea); en la ficha es 1, o se podia llegar a 0 y a negativos.
   */
  minimo?: number;
  alCambiar(cantidad: number): void;
}

export function SelectorCantidad({ cantidad, nombre, minimo, alCambiar }: SelectorCantidadProps) {
  const [escrito, setEscrito] = useState(String(cantidad));

  // El valor puede cambiar desde fuera (la conciliacion al volver a la tienda).
  useEffect(() => {
    setEscrito(String(cantidad));
  }, [cantidad]);

  function confirmar(): void {
    const valor = Number.parseInt(escrito, 10);

    if (Number.isNaN(valor) || valor < 1) {
      setEscrito(String(cantidad));
      return;
    }

    // Acotado aca y no solo en quien recibe: si lo acotado es igual a la
    // cantidad actual, el efecto de arriba no corre y el campo seguiria
    // mostrando lo que se escribio (500 con 99 de verdad).
    const acotado = Math.min(valor, MAXIMO_POR_LINEA);
    setEscrito(String(acotado));
    alCambiar(acotado);
  }

  return (
    <div className="contador">
      <button
        type="button"
        onClick={() => alCambiar(cantidad - 1)}
        disabled={minimo !== undefined && cantidad <= minimo}
        aria-label={`Quitar una unidad de ${nombre}`}
      >
        <Icono nombre="menos" />
      </button>

      <input
        type="text"
        inputMode="numeric"
        value={escrito}
        onChange={(evento) => setEscrito(evento.target.value)}
        onBlur={confirmar}
        onKeyDown={(evento) => {
          if (evento.key === 'Enter') evento.currentTarget.blur();
        }}
        aria-label={`Cantidad de ${nombre}`}
      />

      <button
        type="button"
        onClick={() => alCambiar(cantidad + 1)}
        disabled={cantidad >= MAXIMO_POR_LINEA}
        aria-label={`Agregar una unidad de ${nombre}`}
      >
        <Icono nombre="mas" />
      </button>
    </div>
  );
}
